import type { Variants } from "framer-motion";

import { motion } from "framer-motion";
import { Camera, ChevronLeft, PenTool } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Button } from "react-aria-components";

import { toast } from "@/components/toast/toast-queue";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";

import type { SizeItem } from "./components/size-presets";

import { BriefCard } from "./components/BriefCard";
import { BriefQualityCard } from "./components/BriefQualityCard";
import { CollapsibleCard } from "./components/CollapsibleCard";
import { FilesSection } from "./components/FilesSection";
import { ReviewFooter } from "./components/ReviewFooter";
import {
  BrandGuidelinesCard,
  BriefSummaryCard,
  TechnicalDetailsCard,
} from "./components/SidebarCards";
import { TextForDesign } from "./components/TextForDesign";
import { mapBriefToReview } from "./map-brief";
import { useDesignTypeName } from "./useDesignTypeName";
import { useReviewBrief } from "./useReviewBrief";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.08 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, transition: { duration: 0.35, ease: "easeOut" }, y: 0 },
};

type MBCReviewProps = {
  brandId?: number;
  designTypeId?: number;
  isSavingDraft?: boolean;
  onBack?: () => void;
  /** Brief title (working-memory `title`), so the caller can push it to MySQL (D5). */
  onSaveToDrafts?: (title: null | string) => void;
  /** Fired once the request is submitted, so the caller can navigate away. */
  onSubmitted?: (requestId: number) => void;
  resourceId?: string;
  threadId?: string;
};

export function MBCReview({
  brandId,
  designTypeId,
  isSavingDraft,
  onBack,
  onSaveToDrafts,
  onSubmitted,
  resourceId,
  threadId,
}: MBCReviewProps) {
  const { brief, isSubmitting, patchBrief, quality, submitBrief } =
    useReviewBrief({
      resourceId,
      threadId,
    });
  const { data: brand } = useGetBrandById(brandId ?? 0);
  const designTypeName = useDesignTypeName(designTypeId);

  const data = useMemo(
    () => mapBriefToReview(brief, brand, designTypeName),
    [brief, brand, designTypeName],
  );

  // Edits persist to the working memory only when we have a brief to edit.
  const canEdit = Boolean(threadId);

  const handleSubmit = useCallback(() => {
    submitBrief()
      .then((result) => onSubmitted?.(result.requestId))
      .catch((error: Error) => {
        toast.add({
          description: error.message,
          title: "Couldn't submit your brief",
          type: "error",
        });
      });
  }, [submitBrief, onSubmitted]);

  const handleTitleSave = useCallback(
    (value: string) => void patchBrief({ title: value }),
    [patchBrief],
  );

  const handleVisualDirectionSave = useCallback(
    (value: string) => void patchBrief({ visualDirection: value }),
    [patchBrief],
  );

  const handleTextForDesignSave = useCallback(
    (value: string) => void patchBrief({ textForDesign: value }),
    [patchBrief],
  );

  const handleSizesSave = useCallback(
    (sizes: SizeItem[]) =>
      void patchBrief({
        fileSizes: sizes.map((item) => ({
          dimensions: item.size,
          label: item.label,
        })),
      }),
    [patchBrief],
  );

  return (
    <div className="relative flex min-h-full flex-col">
      {/* Page header */}
      <header className="flex items-center gap-3 border-b border-border-primary bg-bg-primary px-3xl py-4">
        <Button
          aria-label="Go back"
          className="flex size-9 cursor-pointer items-center justify-center rounded-lg border border-border-primary text-text-secondary outline-hidden transition-colors hover:bg-bg-primary-hover"
          onPress={onBack}
        >
          <ChevronLeft className="size-5" />
        </Button>
        <h1 className="text-xl font-semibold text-text-primary">
          Review your brief
        </h1>
      </header>

      {/* Body */}
      <div className="relative flex-1">
        <motion.div
          animate="show"
          className="mx-auto flex max-w-[1240px] flex-col gap-6 px-3xl py-6 lg:flex-row"
          initial="hidden"
          variants={containerVariants}
        >
          {/* Left column */}
          <div className="flex flex-1 flex-col gap-6">
            <motion.div variants={itemVariants}>
              <BriefCard
                brief={data.brief}
                onTitleSave={canEdit ? handleTitleSave : undefined}
                onVisualDirectionSave={
                  canEdit ? handleVisualDirectionSave : undefined
                }
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <TextForDesign
                onSave={canEdit ? handleTextForDesignSave : undefined}
                value={data.brief.textForDesign}
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <CollapsibleCard
                icon={<Camera className="size-5" />}
                title="Visual references"
              >
                <FilesSection
                  files={data.visualReferences.images}
                  links={data.visualReferences.links}
                />
              </CollapsibleCard>
            </motion.div>

            <motion.div variants={itemVariants}>
              <CollapsibleCard
                icon={<PenTool className="size-5" />}
                title="Assets"
              >
                <FilesSection
                  files={data.assets.files}
                  links={data.assets.links}
                />
              </CollapsibleCard>
            </motion.div>
          </div>

          {/* Right column */}
          <div className="flex w-full flex-col gap-6 lg:w-[300px] lg:shrink-0">
            <motion.div variants={itemVariants}>
              <BriefQualityCard quality={quality} />
            </motion.div>
            <motion.div variants={itemVariants}>
              <BriefSummaryCard data={data.briefSummary} />
            </motion.div>
            <motion.div variants={itemVariants}>
              <TechnicalDetailsCard
                fileSizes={data.brief.fileSizes}
                fileTypes={data.brief.fileTypes}
                onSizesChange={canEdit ? handleSizesSave : undefined}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <BrandGuidelinesCard data={data.brandGuidelines} />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <ReviewFooter
        canSubmit={quality?.canSubmit === true}
        isSavingDraft={isSavingDraft}
        isSubmitting={isSubmitting}
        onBackToChat={onBack}
        onSaveToDrafts={
          onSaveToDrafts && (() => onSaveToDrafts(brief?.title ?? null))
        }
        onSubmit={handleSubmit}
      />
    </div>
  );
}
